import { rssFeeds } from '../config/rssFeeds';
import { FeedFetcher } from './feedFetcher';
import { FeedParser } from './feedParser';
import { Storage } from './storage';
import { NewsItem } from './types';
import { UPDATE_INTERVAL } from './constants';
import { defaultFeeds } from './defaultFeeds';

export class FeedUpdater {
  private static instance: FeedUpdater;
  private updateInterval: number | null = null;
  private isUpdating = false;
  private lastUpdate = 0;

  private constructor() {}

  public static getInstance(): FeedUpdater {
    if (!FeedUpdater.instance) {
      FeedUpdater.instance = new FeedUpdater();
    }
    return FeedUpdater.instance;
  }

  public async start(): Promise<void> {
    if (typeof window === 'undefined') {
      console.log('Feed updater cannot run in Node.js environment');
      return;
    }

    try {
      // Initialize storage first
      await Storage.init();

      // Load existing feeds
      const existingFeeds = await Storage.loadFeeds();
      if (existingFeeds.length === 0) {
        // If no feeds exist, save default feeds temporarily
        await Storage.saveFeeds(defaultFeeds);
      }

      // Update feeds if needed
      const now = Date.now();
      if (now - this.lastUpdate >= UPDATE_INTERVAL) {
        await this.updateFeeds();
      }
      
      // Set up periodic updates
      this.updateInterval = window.setInterval(async () => {
        await this.updateFeeds();
      }, UPDATE_INTERVAL);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error starting feed updater';
      console.error('Error starting feed updater:', errorMessage);
      throw error;
    }
  }

  private async updateFeeds(): Promise<void> {
    if (this.isUpdating) {
      throw new Error('Update already in progress');
    }
    
    this.isUpdating = true;
    
    try {
      const feedPromises = rssFeeds.map(async (feed) => {
        try {
          const xml = await FeedFetcher.fetchFeed(feed.url);
          return FeedParser.parseFeed(xml, feed);
        } catch (error) {
          console.error(`Error fetching feed ${feed.name}:`, error);
          return [];
        }
      });

      const results = await Promise.all(feedPromises);
      const newItems = results.flat().sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      if (newItems.length === 0) {
        throw new Error('No feeds were successfully fetched');
      }

      await Storage.saveFeeds(newItems);
      this.lastUpdate = Date.now();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error updating feeds';
      console.error('Error updating feeds:', errorMessage);
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  public async forceUpdate(): Promise<void> {
    if (this.isUpdating) {
      throw new Error('Update already in progress');
    }

    try {
      await Storage.init();
      await this.updateFeeds();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error during force update';
      console.error('Feed refresh error:', errorMessage);
      throw error;
    }
  }

  public stop(): void {
    if (this.updateInterval !== null) {
      window.clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }
}
